/**
 * Repeatedly decodes a pathname to catch encoded traversal attempts that may be
 * nested more than once.
 *
 * @param {string} pathname Pathname portion of a request URL.
 * @returns {string} Fully decoded pathname.
 * @throws {TypeError} If decoding fails.
 */
export const decodePathname = pathname => {
  let decoded = pathname;

  for (let i = 0; i < 5; i += 1) {
    try {
      const nextValue = decodeURIComponent(decoded);
      if (nextValue === decoded) {
        return decoded;
      }
      decoded = nextValue;
    } catch (error) {
      throw new TypeError('Invalid request path');
    }
  }

  return decoded;
};

/**
 * Validates that a client-side API request path stays inside the local `/api/`
 * namespace and does not contain traversal, control characters, or malformed
 * path segments.
 *
 * @param {string} path Request path passed to the API helper.
 * @returns {string} Original request path if valid.
 * @throws {TypeError} If the path is invalid.
 */
export const validateRequestPath = path => {
  const requestPath = String(path);
  const queryIndex = requestPath.indexOf('?');
  const rawPathname = queryIndex >= 0 ? requestPath.slice(0, queryIndex) : requestPath;
  const search = queryIndex >= 0 ? requestPath.slice(queryIndex) : '';

  if (!rawPathname.startsWith('/')) {
    throw new TypeError('Invalid request path');
  }

  const decodedPathname = decodePathname(rawPathname);
  const hasControlCharacters = /[\u0000-\u001F\u007F]/.test(decodedPathname);

  if (hasControlCharacters || !decodedPathname.startsWith('/api/') || decodedPathname.includes('\\')) {
    throw new TypeError('Invalid request path');
  }

  const segments = decodedPathname.split('/');
  const hasInvalidSegment = segments.some((segment, index) => {
    if (index === 0) {
      return segment !== '';
    }

    return segment.length === 0 || segment === '.' || segment === '..';
  });

  if (hasInvalidSegment) {
    throw new TypeError('Invalid request path');
  }

  return `${decodedPathname}${search}`;
};
